import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  getVaultDisplayOrder,
  reorderVaults,
  type Vault,
  type VaultDisplayOrder,
  type VaultKind,
} from 'src/pages/admin/services/vaultService';

import { VAULTS_NS as NS } from './constants';
import { inConsoleOrder, type KindOrders, orderErrorMessage, reorderedIds } from './helpers';

const toKindOrder = (o: VaultDisplayOrder) => ({
  revision: o.revision,
  orderedVaultIds: o.orderedVaultIds,
});

/**
 * XBIT-4247 (PRD §14.4 Ordering): the list in the order operations set, and the drop that changes it. Both kinds'
 * orders are read once (GET /vault/vaults/order); the list is arranged by them (inConsoleOrder). A drop sends the
 * kind's whole new list under the revision last read, the row staying where it was dropped while the write runs; a
 * refusal or a failure says why, then reads the orders and the list again, so what shows is what the engine holds.
 * One write at a time: a drop made while one runs is ignored, so the revision chain stays one step at a time. Until the
 * orders are read no kind can be reordered (a failed read leaves the engine's order and no handles).
 */
export const useVaultOrder = ({
  vaults,
  refetch,
}: {
  vaults: Vault[];
  refetch: () => Promise<unknown> | void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [orders, setOrders] = useState<KindOrders>({});
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  // react-admin's translate is not identity-stable; a ref keeps the callbacks stable (see useVaults).
  const translateRef = useRef(translate);
  translateRef.current = translate;

  const loadOrders = useCallback(async () => {
    try {
      const [official, community] = await Promise.all([
        getVaultDisplayOrder('OFFICIAL'),
        getVaultDisplayOrder('COMMUNITY'),
      ]);
      setOrders({ OFFICIAL: toKindOrder(official), COMMUNITY: toKindOrder(community) });
    } catch {
      setOrders({});
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const ordered = useMemo(() => inConsoleOrder(vaults, orders), [vaults, orders]);

  const drop = useCallback(
    async (fromId: string, toId: string) => {
      if (inFlight.current) return;
      const plan = reorderedIds(ordered, orders, fromId, toId);
      const current = plan ? orders[plan.kind] : undefined;
      if (!plan || !current) return;
      inFlight.current = true;
      setSaving(true);
      setOrders((prev) => ({
        ...prev,
        [plan.kind]: { ...current, orderedVaultIds: plan.orderedVaultIds },
      }));
      try {
        const saved = await reorderVaults(plan.kind, plan.orderedVaultIds, current.revision);
        setOrders((prev) => ({ ...prev, [plan.kind]: toKindOrder(saved) }));
        notify(translateRef.current(`${NS}.order.saved`), { type: 'success' });
      } catch (error) {
        notify(orderErrorMessage(error, translateRef.current), { type: 'error' });
        await Promise.all([loadOrders(), refetch()]);
      } finally {
        inFlight.current = false;
        setSaving(false);
      }
    },
    [ordered, orders, loadOrders, refetch, notify]
  );

  const canReorder = useCallback((kind: VaultKind) => orders[kind] !== undefined, [orders]);

  return { vaults: ordered, saving, canReorder, drop };
};
